/**
 * (Lives in `@zuruflex/core`, not the website, because the dashboard's seed
 * and the website's fallback both read it, and neither may import from
 * `apps/web`.)
 *
 * The help articles the partner app links out to, as content rather than as
 * translations.
 *
 * Same reasoning as `@zuruflex/core/legal`, for a different reason at the end of
 * it. These pages describe what the software does — which photographs a
 * listing cannot go live without, what `partnerEarnings` is arithmetic on,
 * when a handover record seals itself — and they change when that changes.
 * Routing them through `@zuruflex/i18n` would put the sentence that has to be
 * corrected behind two translations nobody in the building can check, and
 * the key-parity test would then hold the correction back until all three
 * catalogues had it. The page *chrome* — the eyebrow, the contents rail, the
 * "last updated" line — is translated as usual, from `web:partnerHelp.*`.
 *
 * The five slugs are fixed by the phone: `app/account/help.tsx` opens
 * `<webUrl>/help/partners/<slug>`, so a slug renamed here 404s an app
 * somebody installed last month and will not update. Titles match
 * `partner:settings.app.support.topic.*`, which is what the phone lists.
 *
 * Every factual claim below is a claim about code in this repository. Where
 * the platform has no policy — a payout schedule, a late-return rate — the
 * article says what the system holds and hands the rest to support. A help
 * page that states a rule Zuruflex does not implement is worse than a short one.
 */
/** Where these pages live. One constant, so the rail and the router agree. */
export const PARTNER_HELP_BASE = '/help/partners';
const UPDATED = '2026-09-21';
const LISTING = {
    slug: 'listing',
    title: 'Getting a car live',
    lead: 'What Zuruflex checks before a car can be booked, and the order worth doing it in.',
    updated: UPDATED,
    sections: [
        {
            heading: 'A car is added on the web, not on the phone',
            body: [
                'Cars are created in your partner account on the Zuruflex website. That is also where the registration, insurance and roadworthiness certificates are filed against the car, and those are documents you scan sitting at a desk rather than in a yard.',
                'The app is for the car once it exists: finishing the photographs, setting it live, taking it down, and running the hires. A car you add on the website appears on the phone without anything else being done.',
                'Your account has to be approved before any car of yours can be submitted at all — the API refuses the submission outright. If yours is not yet, start with verification and paperwork.',
            ],
        },
        {
            heading: 'Three photographs are compulsory',
            body: [
                'A listing cannot go live without all three of these. Between them they cover all four sides of the car and what the driver sees, which is the least somebody needs to choose one car over another.',
            ],
            rows: [
                {
                    term: 'Front three-quarter',
                    detail: 'The hero shot. Front and one side in the same frame.',
                },
                { term: 'Rear three-quarter', detail: 'The opposite corner, so the other two sides show.' },
                { term: 'Dashboard', detail: 'What the driver sees from the seat.' },
            ],
        },
        {
            heading: 'And the rest are worth taking anyway',
            body: [
                'Seats, boot, a feature worth naming, the odometer and any damage already on the car are all accepted and none of them hold the listing up. The damage shots are the ones that pay for themselves: a mark photographed before the car ever went out is a mark nobody argues about later.',
                'Deleting a photograph that takes the car back below the three compulsory kinds pulls a live listing down to draft on the spot. That is deliberate — it goes back up with the replacement rather than staying live with a gap — so replace before you delete when you can.',
            ],
        },
        {
            heading: 'A price it can be quoted at',
            body: [
                'A daily rate above zero is the other half of the gate: a listing without one cannot be quoted, so it cannot be published.',
                'If the car is offered with a driver, the driver’s daily rate has to be set too. For a car that is only ever let with a driver that figure is part of the advertised price, so a listing missing it would be advertising the wrong number.',
                'The deposit is set per car and is optional. It is quoted to the renter as its own line rather than folded into the daily rate, and it is not part of what you earn — see the payouts page.',
            ],
        },
        {
            heading: 'The paperwork that rides with the car',
            body: [
                'Three documents belong to the car itself rather than to your account: the vehicle registration, the insurance certificate and the roadworthy certificate. Each one is filed with an expiry date — the API will not accept those three without one — and an expired certificate counts as a missing one.',
                'The automatic gate on publishing is the photographs and the rates. The documents are what the review looks at, and a car submitted without them will not come back published. Filing them first saves you the round trip.',
            ],
        },
        {
            heading: 'Submit, review, live',
            body: [
                'Submitting puts the car in review. Zuruflex either publishes it or sends it back with a note saying what to fix — a rejection cannot be recorded without one, so there is always a reason to read.',
                'Published is not quite the same as visible. A car reaches customers when it is published and your account is in good standing — and a suspension does more than hide the fleet. Every car of yours that was live is written down to unlisted in the same moment, and lifting the suspension does not put them back up: you relist them yourself, one at a time, and a car that has been live before goes straight back to live.',
            ],
        },
        {
            heading: 'What sends a live car back through review',
            body: [
                'Edits a customer would notice: make, model, year, plate, class, transmission, fuel, seats, doors, the daily rate, the deposit, the driver policy, the driver rate, the description and the pick-up point. Change any of those on a published car and it returns to the queue.',
                'Photographs and the calendar do not. Blocking dates, adding pictures and taking bookings all happen on a live listing without interrupting it.',
                'Taking a car down is reversible and cheap. One that has been live before goes straight back to live when you put it up again, provided it still clears the bar; one that has never been live joins the review queue for the first time.',
            ],
        },
    ],
};
const PAYOUTS = {
    slug: 'payouts',
    title: 'When payouts land',
    lead: 'How your share of a hire is worked out, when it counts as earned, and what the system does not decide.',
    updated: UPDATED,
    sections: [
        {
            heading: 'You are paid on the subtotal, not the total',
            body: [
                'A booking’s total is three things added together, and only the first has anything to do with you.',
            ],
            rows: [
                {
                    term: 'Subtotal',
                    detail: 'The hire itself: the daily rate across the days, the driver if there is one, less any long-hire discount. This is what your share is worked out from.',
                },
                {
                    term: 'Tax',
                    detail: 'Applied on top of the subtotal at the rate set for the country. Not yours.',
                },
                {
                    term: 'Deposit',
                    detail: 'The renter’s own money passing through, held against damage, fuel and fines, and returned to them. Never part of what you earn.',
                },
            ],
        },
        {
            heading: 'Commission is fixed the moment the booking is made',
            body: [
                'Your commission rate is copied onto the booking when it is created, and the split is worked out there and then and stored with it. Your earnings are the subtotal less that commission.',
                'That snapshot is the point. If your rate is renegotiated in February, January’s bookings keep January’s rate — reading the rate off your account at payout time would quietly restate a month you had already been paid for. A new rate applies to bookings made after it changes, and never to ones already taken.',
                'The accept screen in the app shows all three numbers — the hire subtotal, the commission being subtracted and what you get — before you accept anything. So does the booking afterwards.',
            ],
        },
        {
            heading: 'A hire is earned when it is completed',
            body: [
                'The path is: a request arrives, you accept it, the renter pays, you hand over the keys, and you record the return. Only that last step makes a hire count. A booking you accepted but nobody paid for has earned nothing, and neither has one that is still out.',
                'The figure on the app’s overview is this month’s completed hires, added up by their drop-off date. It is not a balance and it is not a statement — it is what the hires you finished this month came to.',
            ],
        },
        {
            heading: 'What the system does not decide: when the money moves',
            body: [
                'Zuruflex does not run payouts from inside the product yet. There is no payout run, no statement, no schedule and no balance anywhere in the system, and this page is not going to invent one.',
                'What the system does hold, per hire and to the minor unit, is the subtotal, the commission rate that applied, the commission taken and your earnings. That is the record a payout is reconciled against. Timing, frequency and anything held back are arranged with partner support, and the money goes to the payout account you named at onboarding — the mobile-money wallet or bank account in your verified name.',
            ],
        },
        {
            heading: 'If a figure looks wrong',
            body: [
                'Open the booking. It carries its own subtotal, its own commission percentage and its own earnings, all stamped at the time it was made, so the three either add up or they do not.',
                'A hire that looks short next to another is usually a discount on a longer booking or a different commission rate on an older one. Send support the booking reference — the ZF- code — rather than a screenshot of the month; the reference is what they can look the whole thing up by.',
            ],
        },
    ],
};
const DEPOSITS = {
    slug: 'deposits',
    title: 'Damage, deposits and disputes',
    lead: 'What the deposit covers, what the handover record is for, and why the ten minutes at the car decide the argument.',
    updated: UPDATED,
    sections: [
        {
            heading: 'The deposit',
            body: [
                'The deposit is set per car, quoted to the renter as its own line, and added to the booking total. It is held against damage, missing fuel and fines, and it is returned to the renter when the car comes back as it went out.',
                'Zuruflex collects it, so Zuruflex returns it or holds it back. How much is taken before pick-up is a Zuruflex setting that differs by country — sometimes the whole total, sometimes the deposit alone — so a booking marked paid does not always mean every part of it has been collected. Support can tell you what has actually moved on a given booking.',
            ],
        },
        {
            heading: 'The handover record is what settles it',
            body: [
                'Both ends of the hire get a record: one when the keys go out, one when the car comes back. Each carries the same things, which is what makes the pair worth having.',
            ],
            rows: [
                {
                    term: 'Odometer',
                    detail: 'Whole kilometres off the dash. The distance driven is the difference between the two, so neither reading is ever a guess.',
                },
                { term: 'Fuel level', detail: 'Where the needle actually sits, at both ends.' },
                { term: 'The checklist', detail: 'What you ticked on the day, stored as ticked.' },
                {
                    term: 'Notes',
                    detail: 'Marks already on the car, in your own words, before it leaves.',
                },
                {
                    term: 'Four photographs',
                    detail: 'Front three-quarter, rear three-quarter, interior and dashboard — the same four angles at both ends, so the two sets can be put side by side. That is the only reason to photograph a car at all.',
                },
            ],
        },
        {
            heading: 'It seals itself, and that is the point',
            body: [
                'A pick-up record can be written and corrected all the while the booking is confirmed. The moment you start the hire it is sealed, and nothing — not you, not Zuruflex — edits it afterwards. The return record works the same way one step later.',
                'So show the renter the record on your phone before you start the hire. They are looking at the same four photographs and the same odometer reading you are, and both of you know it cannot be changed after that. A record agreed at the car is worth more than an argument three days later.',
                'Two things the system simply refuses: a return recorded with no pick-up behind it, and a closing odometer lower than the opening one. Both are typos or guesses, and both turn into a negative charge if they are let through.',
            ],
        },
        {
            heading: 'Who can see it',
            body: [
                'The photographs are evidence, not gallery pictures. They are stored privately and served only through short-lived signed links, and the record is readable by you and by Zuruflex staff — not from the renter’s own account.',
                'The renter gets it the way they signed for it: on your phone, at the car, at both ends. If they ask for a copy afterwards, ask support and they will send it. Do not paste a photo link into a chat expecting it to still work later; it will not.',
            ],
        },
        {
            heading: 'When something is wrong at the return',
            body: [
                'Record the return as you find it — the real odometer, the real fuel level, the damage photographed from the angle that shows it — and write what you see in the notes. Then say it to the renter in the booking’s own thread, before they have left if you can, and raise it with Zuruflex on the partner support line — WhatsApp or email, both on the app’s help screen.',
                'Deciding what comes out of a deposit is a person’s call, not the system’s, and returning money is done by hand in the payment provider. That cuts both ways: nothing is deducted automatically, and nothing is refunded automatically either, so raising it early is what gets it settled while the deposit is still held.',
                'Keep it in the thread rather than on a phone call. The thread is between you and the renter, but it is also the transcript the decision gets made from: Zuruflex staff can read a booking’s thread back when you bring them the reference, and a phone call leaves nothing to read.',
            ],
        },
    ],
};
const LATE_RETURNS = {
    slug: 'late-returns',
    title: 'A renter is late bringing the car back',
    lead: 'The car is not back, the hire is still open, and the next booking is coming.',
    updated: UPDATED,
    sections: [
        {
            heading: 'Message them in the booking’s thread',
            body: [
                'Every booking has a thread. It reaches the renter rather than Zuruflex — support is a separate channel, on WhatsApp or by email — but use it rather than a private call anyway: it is the record everything afterwards is decided from, and staff can read it back against the booking reference instead of you retelling it.',
                'On a confirmed hire, phone numbers and email addresses pass through the thread unmasked. Before a booking is confirmed they are stripped out — that is what stops a booking being taken off the platform — so if you are in a confirmed hire you can simply exchange numbers there and ring.',
            ],
        },
        {
            heading: 'Tell Zuruflex early, not once it is a problem',
            body: [
                'Half an hour late with a message is nothing. Two hours late with no answer is a booking someone else is about to lose.',
                'Look at what the car is due to do next. If there is another hire on it, tell support while there is still time to move that renter or put them in a different car. Once the next customer is standing at your gate, the options are worse and the one who paid you both is the one who suffers.',
            ],
        },
        {
            heading: 'Do not record the return until the car is in front of you',
            body: [
                'The return record is a record of the car, not of the booking: the odometer, the fuel level and the four photographs have to be the real ones. Recording it early to tidy the list up seals the wrong numbers, permanently.',
                'Until you record it, the hire stays open. The calendar does not read that, though — availability is worked out from the dates on the booking, so the car comes free at the drop-off time that was agreed whether or not it is back on your forecourt. An overdue car can be booked by somebody else while it is still out, which is why the message to support above matters more than the record: tell them before the next hire is taken.',
            ],
        },
        {
            heading: 'What it costs the renter',
            body: [
                'Honestly: there is no automatic late fee in this system. Nothing charges by the hour, nothing prices extra kilometres at the return, and no deduction is made without a person making it.',
                'What the platform gives you is the evidence for the claim rather than the claim itself — the agreed drop-off time on the booking, the two odometer readings, the two fuel levels and the two sets of photographs. Anything you want charged for the extra time or distance is agreed with support and taken from the deposit before it goes back. Raise it while the deposit is still held.',
                'The rental terms say a late return is chargeable and that the rate is still to be confirmed. Until that rate exists, quoting a renter a figure of your own is a figure Zuruflex cannot stand behind.',
            ],
        },
        {
            heading: 'If it is not coming back at all',
            body: [
                'Unreachable renter, a car that has left the country, a story that keeps changing: stop chasing it yourself and call support. Zuruflex holds the identity documents the renter was verified with and the payment record behind the booking, and neither is yours to act on.',
                'Do not record a return you have not seen, and do not mark the hire finished to clear it off the screen. The open booking is the thing that says the car is still out, and it is the anchor for everything that follows.',
            ],
        },
    ],
};
const VERIFICATION = {
    slug: 'verification',
    title: 'Verification and paperwork',
    lead: 'What Zuruflex needs on file before your account is approved, and what has to stay current afterwards.',
    updated: UPDATED,
    sections: [
        {
            heading: 'Four steps, two of which block you',
            body: [
                'Onboarding has four steps, and only the first two stand between you and a review. Zuruflex cannot approve a partner it has not identified — but it can perfectly well approve one who has not yet named a bank account or drafted a car, and holding the review up over work you can finish afterwards helps nobody.',
            ],
            rows: [
                {
                    term: 'Your particulars — required',
                    detail: 'A company gives its legal name and registration number. An individual owner gives a contact email address.',
                },
                {
                    term: 'Documents — required',
                    detail: 'A company files its business registration. An individual files an ID card. Those are the only two that gate the review.',
                },
                {
                    term: 'Payout details',
                    detail: 'Mobile money or bank transfer, with the account number and the account name. Needed before you are paid, not before you are approved.',
                },
                {
                    term: 'A car',
                    detail: 'At least one on the account. It does not have to be finished or live.',
                },
            ],
        },
        {
            heading: 'What "verified" actually means',
            body: [
                'It is worked out from the documents on file rather than typed in by anybody, which is why nobody can verify an account that has filed nothing and why approving the last outstanding certificate verifies the account in the same breath.',
                'Verified is the narrow case: every required document present, approved and unexpired. A rejected document makes the account rejected. An expired one puts it back to pending rather than rejected — nobody did anything wrong, a fresh copy is simply owed.',
            ],
        },
        {
            heading: 'Submitting, and what approval unlocks',
            body: [
                'Once the two blocking steps are done you can ask to be reviewed. Submitting does not change what Zuruflex sees; it starts the clock and puts you in the queue.',
                'Approval is what lets a car be submitted for listing at all — an unapproved account is refused at that step — and it is also what keeps the fleet on the market. Suspending an account takes every live car of yours down to unlisted, and approving it again does not reverse that: the cars have to be put back up by hand once the account is in good standing again.',
                'How long a review takes is not something the system promises, so this page will not either. Support will tell you where yours is.',
            ],
        },
        {
            heading: 'Paperwork per car',
            body: [
                'Separate from your account, and deliberately so: a roadworthy certificate should never hold up an account, and an account review should never hold up a car.',
            ],
            rows: [
                {
                    term: 'Vehicle registration',
                    detail: 'The car’s own registration document, with the plate readable.',
                },
                { term: 'Insurance certificate', detail: 'Current cover on that car.' },
                { term: 'Roadworthy certificate', detail: 'Current, for that car.' },
            ],
        },
        {
            heading: 'Expiry dates, and why they are compulsory',
            body: [
                'All three of the car documents are filed with an expiry date, and the API refuses them without one. A certificate that does not say when it lapses cannot be checked against the day of a hire, which makes it useless for the one thing it is for.',
                'Replace them before they lapse rather than after. Filing a new copy is the same upload; there is no separate renewal, and the newest document of each kind is the one that counts.',
                'You can file paperwork either way. Scanning at a desk in your partner account on the website is easier for multi-page documents; the app photographs them with a shape guide for an A4 certificate or an ID card, which is quicker when the certificate is in the glovebox and you are standing at the car.',
            ],
        },
    ],
};
/** Every article, in the order the contents rail and the phone list them. */
export const PARTNER_HELP_ARTICLES = [
    LISTING,
    PAYOUTS,
    DEPOSITS,
    LATE_RETURNS,
    VERIFICATION,
];
export function partnerHelpArticle(slug) {
    return PARTNER_HELP_ARTICLES.find((article) => article.slug === slug);
}
